import { Modal, ModalDialog, Stack } from '@mui/joy';
import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';

import GFormProvider from 'components/technical/form/GFormProvider.tsx';
import { GraphQLApiFormErrorMessage } from 'components/technical/form/GraphQLApiErrorMessage.tsx';
import gYupResolver from 'components/technical/form/gYupResolver.ts';
import SubmitButton from 'components/technical/form/SubmitButton.tsx';
import { useGraphQLApiError } from 'components/technical/form/UseGraphQLApiError.tsx';
import GDialogHeader from 'components/technical/GDialog/GDialogHeader.tsx';
import { FormInput } from 'components/technical/inputs';
import { useFeedback } from '../../../../technical/Feedback/UseFeedback.tsx';
import {
  type ICreateRebalancingRuleInput,
  IPortfolioDefinitionSubType,
  IPortfolioDefinitionType,
  type IUserPortfolioDefinitionType,
  PortfolioDefDashboardInputDocument,
  useCreatePortfolioDefinitionMutation,
  useUpdatePortfolioDefinitionMutation,
  type IMultiFactor,
  type IRebalancedPortfolioMultifactorRuleInput,
} from '../../../../../generated/graphql.tsx';
import type { AccountLabelInputAccount } from '../../../../portfolio/account/AccountLabel.tsx';
import { createAccountIdAutocompleteOptions } from '../../../../portfolio/account/AccountService.tsx';
import FormStaticMultiAutocomplete from '../../../../technical/form/FormStaticMultiAutocomplete.tsx';
import { formSchema as extraAccountSchema } from '../realPortfolio/CreateRealPortfolioDialog.validation.tsx';
import {
  createSubfundIdAutocompleteOptions,
  type SubfundAutocompleteOptionInput,
} from '../../../../portfolio/fund/SubFundService.tsx';
import FormErrorMessage from '../../../../technical/form/FormErrorMessage.tsx';
import TransactionBasedPortfolioFormFields from '../rebalancedPortfolio/TransactionBasedPortfolioFormFields.tsx';
import {
  type TransactionBasedFormOutputFields,
  transactionBasedSchema,
} from '../rebalancedPortfolio/transactionBasedPortfolioSchema.validation.ts';
import MultifactorBasedPortfolioFormFields, {
  createMultifactorBasedPortfolioInput,
} from '../rebalancedPortfolio/MultifactorBasedPortfolioFormFields.tsx';
import {
  type MultifactorBasedFormInputFields,
  type MultifactorBasedFormOutputFields,
  multifactorBasedSchema,
} from '../rebalancedPortfolio/MultifactorBasedPortfolioSchema.validation.ts';
import * as yup from 'yup';
import type { FormInputType } from 'components/technical/form/Form.types.ts';
import isNil from 'lodash/fp/isNil';
import { Add } from '@mui/icons-material';
import type { PortfolioDefActionOptimizerRule } from '../PortfolioDefinitionActions.tsx';

type FormOutputFields = {
  name: string;
  description: string;
};

const basePortfolioSchema = yup.object({
  name: yup.string().required(),
  description: yup.string(),
});

type FormInputFields = FormInputType<FormOutputFields>;

const rebalancedSubTypeToSchema: Partial<
  Record<IPortfolioDefinitionSubType, yup.ObjectSchema<Record<string, unknown>>>
> = {
  [IPortfolioDefinitionSubType.TransactionBased]: transactionBasedSchema,
  [IPortfolioDefinitionSubType.MultifactorBased]: multifactorBasedSchema,
};

const getSchema = (
  type: IPortfolioDefinitionType,
  subType: IPortfolioDefinitionSubType
): yup.ObjectSchema<Record<string, unknown>> => {
  let schema: yup.ObjectSchema<Record<string, unknown>> = basePortfolioSchema;
  if (type === IPortfolioDefinitionType.Real) {
    schema = schema.concat(extraAccountSchema);
  } else if (type === IPortfolioDefinitionType.Rebalanced) {
    const subTypeSchema = rebalancedSubTypeToSchema[subType];

    if (subTypeSchema) {
      schema = schema.concat(subTypeSchema);
    }
  }
  return schema;
};

type FullFormInputFields = FormInputFields & {
  accounts: string[];
  subFunds: number[];
} & FormInputType<TransactionBasedFormOutputFields> &
  MultifactorBasedFormInputFields;

type FullFormOutputFields = FormOutputFields & {
  accounts: string[];
  subFunds: number[];
} & TransactionBasedFormOutputFields &
  MultifactorBasedFormOutputFields;

export type CreateUpdatePortfolioDialogDefProps = {
  id?: string;
  name?: string;
  description?: string;
  type: IPortfolioDefinitionType;
  subType: IPortfolioDefinitionSubType;
  realDefAccounts: AccountLabelInputAccount[];
  realDefSubFunds: SubfundAutocompleteOptionInput[];
  rebalancedDetails?: {
    defaultAssetComposition: { id: string; weight: number }[];
    transactionTag?: string | null | undefined;
    multifactorRule?:
      | (Partial<Omit<IRebalancedPortfolioMultifactorRuleInput, 'multifactorId'>> & {
          multifactorId?: number | null | undefined;
        })
      | null
      | undefined;
    optimizerRule?: PortfolioDefActionOptimizerRule | null | undefined;
  } | null;
  rebalancingRules?: ICreateRebalancingRuleInput[];
};

const CreateUpdatePortfolioDialog = ({
  onClose,
  def,
  onCompleted,
  accounts,
  subFunds,
  multifactors,
}: {
  onClose: () => void;
  onCompleted: (id: string) => void;
  def: CreateUpdatePortfolioDialogDefProps;
  multifactors: {
    maxFactors: number;
    multifactor: Pick<IMultiFactor, 'id' | 'name'>;
  }[];
  accounts?: AccountLabelInputAccount[];
  subFunds?: SubfundAutocompleteOptionInput[];
}): ReactElement => {
  const create = isNil(def.id);
  const schema = getSchema(def.type, def.subType);

  const methods = useForm<FullFormInputFields>({
    resolver: gYupResolver(schema),
    defaultValues: {
      name: def?.name ?? 'Portfolio 1',
      description: def?.description ?? '',
      accounts: (def?.realDefAccounts ?? []).map((acc) => acc.id),
      subFunds: (def?.realDefSubFunds ?? []).map((subFund) => subFund.id),
      tag: def?.rebalancedDetails?.transactionTag ?? '',
      longAssets: def?.rebalancedDetails?.multifactorRule?.longAssets?.toString() ?? '0',
      shortAssets: def?.rebalancedDetails?.multifactorRule?.shortAssets?.toString() ?? '0',
      minFactors: def?.rebalancedDetails?.multifactorRule?.minFactors?.toString() ?? '0',
      multifactor: multifactors.find(
        (m) => m.multifactor.id === def?.rebalancedDetails?.multifactorRule?.multifactorId
      ),
    },
    mode: 'onChange',
  });

  const { showSuccessMessage } = useFeedback();

  const { onErrorAndThrow } = useGraphQLApiError(methods);

  const [mutate] = useUpdatePortfolioDefinitionMutation({
    refetchQueries: [PortfolioDefDashboardInputDocument],
  });

  const [createPortfolioDef] = useCreatePortfolioDefinitionMutation({
    refetchQueries: [PortfolioDefDashboardInputDocument],
  });

  const handleFormSubmit = async (data: FullFormInputFields): Promise<void> => {
    const output = data as unknown as FullFormOutputFields;
    try {
      if (create) {
        const { data: responseData } = await createPortfolioDef({
          variables: {
            input: {
              name: output.name,
              description: output.description,
              type: def.type as unknown as IUserPortfolioDefinitionType,
              subType: def.subType,
              realDefAccounts: output.accounts,
              realDefSubFunds: output.subFunds,
              composition: def.rebalancedDetails?.defaultAssetComposition ?? [],
              tag: output.tag,
              multifactorRule:
                def.subType === IPortfolioDefinitionSubType.MultifactorBased
                  ? {
                      ...createMultifactorBasedPortfolioInput(output),
                      filters: def.rebalancedDetails?.multifactorRule?.filters ?? [],
                    }
                  : undefined,
              rebalancingRules: def.rebalancingRules ?? [],
            },
          },
        });

        showSuccessMessage('Portfolio successfully created');
        onCompleted(responseData?.portfolio?.createDefinition!);
      } else {
        await mutate({
          variables: {
            input: {
              id: def.id!,
              name: output.name,
              description: output.description,
              realDefAccounts: output.accounts,
              realDefSubFunds: output.subFunds,
              composition: def.rebalancedDetails?.defaultAssetComposition ?? [],
              transactionTag: output.tag,
              multifactorRule:
                def.subType === IPortfolioDefinitionSubType.MultifactorBased
                  ? {
                      ...createMultifactorBasedPortfolioInput(output),
                      filters: def.rebalancedDetails?.multifactorRule?.filters ?? [],
                    }
                  : undefined,
            },
          },
        });

        showSuccessMessage('Portfolio successfully updated');
        onCompleted(def.id!);
      }
    } catch (e) {
      onErrorAndThrow(e);
    }
  };

  const accountOptions = createAccountIdAutocompleteOptions(accounts ?? []);
  const subFundOptions = createSubfundIdAutocompleteOptions(subFunds ?? []);
  const width = 'xl3';
  return (
    <Modal open={true} onClose={onClose}>
      <ModalDialog minWidth="25rem">
        <GFormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleFormSubmit)}>
            <GDialogHeader>
              {create ? 'Create' : 'Update'} {def.name} portfolio
            </GDialogHeader>
            <Stack spacing={3} alignItems={'center'}>
              <FormInput label="Name" name="name" width={width} />
              <FormInput label="Description" name="description" width={width} />
              {def.type === IPortfolioDefinitionType.Real && (
                <>
                  <FormStaticMultiAutocomplete
                    {...accountOptions}
                    name="accounts"
                    label="Accounts"
                    width={width}
                    onChange={(_val, { clearErrors }) => {
                      clearErrors('notEmptyAccountOrSubfunds');
                    }}
                  />
                  <FormStaticMultiAutocomplete
                    {...subFundOptions}
                    name="subFunds"
                    label="Sub-funds"
                    width={width}
                    onChange={(_val, { clearErrors }) => {
                      clearErrors('notEmptyAccountOrSubfunds');
                    }}
                  />
                </>
              )}
              {def.type === IPortfolioDefinitionType.Rebalanced &&
                def.subType === IPortfolioDefinitionSubType.TransactionBased && (
                  <TransactionBasedPortfolioFormFields width={width} />
                )}
              {def.type === IPortfolioDefinitionType.Rebalanced &&
                def.subType === IPortfolioDefinitionSubType.MultifactorBased && (
                  <MultifactorBasedPortfolioFormFields width={width} multifactors={multifactors} />
                )}
              <Stack alignItems="center" spacing={1.5}>
                <FormErrorMessage name={'notEmptyAccountOrSubfunds'} />
                <GraphQLApiFormErrorMessage />
                <SubmitButton width="xl2" startDecorator={create ? <Add /> : undefined}>
                  {create ? 'Create' : 'Update'}
                </SubmitButton>
              </Stack>
            </Stack>
          </form>
        </GFormProvider>
      </ModalDialog>
    </Modal>
  );
};

export default CreateUpdatePortfolioDialog;
